interface LoadingSkeletonProps {
  variant?: "card" | "table" | "chart" | "text" | "circle";
  width?: string;
  height?: string;
  count?: number;
  className?: string;
}

export default function LoadingSkeleton({
  variant = "text",
  width = "100%",
  height,
  count = 1,
  className = "",
}: LoadingSkeletonProps) {
  const getSkeletonClasses = () => {
    const baseClasses = "animate-pulse bg-gray-300 rounded";

    switch (variant) {
      case "card":
        return `${baseClasses} ${className || "h-48 w-full"}`;
      case "table":
        return `${baseClasses} ${className || "h-12 w-full"}`;
      case "chart":
        return `${baseClasses} ${className || "h-64 w-full"}`;
      case "circle":
        return `${baseClasses} rounded-full ${className || "h-12 w-12"}`;
      case "text":
      default:
        return `${baseClasses} ${className || "h-4 w-full"}`;
    }
  };

  const skeletonStyle = {
    width: className ? undefined : width,
    height: className ? undefined : height,
  };

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={getSkeletonClasses()} style={skeletonStyle} />
      ))}
    </>
  );
}

// Pre-built skeleton components for common use cases
export function CardSkeleton({ count = 1 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-white rounded-lg shadow-md p-6">
          <LoadingSkeleton variant="text" width="60%" height="24px" className="mb-4" />
          <LoadingSkeleton variant="text" count={3} className="mb-2" />
          <LoadingSkeleton variant="chart" className="mt-4" />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <LoadingSkeleton variant="text" width="40%" height="20px" />
      </div>
      <div className="divide-y divide-gray-200">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="p-4 flex items-center gap-4">
            <LoadingSkeleton variant="circle" />
            <div className="flex-1 space-y-2">
              <LoadingSkeleton variant="text" width="80%" />
              <LoadingSkeleton variant="text" width="60%" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ActivityCardSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
          <LoadingSkeleton variant="card" height="200px" className="rounded-none" />
          <div className="p-4 space-y-3">
            <LoadingSkeleton variant="text" width="70%" height="20px" />
            <div className="flex gap-4">
              <LoadingSkeleton variant="text" width="30%" />
              <LoadingSkeleton variant="text" width="30%" />
              <LoadingSkeleton variant="text" width="30%" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ChartSkeleton() {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <LoadingSkeleton variant="text" width="40%" height="24px" className="mb-6" />
      <LoadingSkeleton variant="chart" height="300px" />
      <div className="mt-4 flex gap-4">
        <LoadingSkeleton variant="text" width="20%" />
        <LoadingSkeleton variant="text" width="20%" />
        <LoadingSkeleton variant="text" width="20%" />
      </div>
    </div>
  );
}

export function PageLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="container mx-auto max-w-6xl">
        <LoadingSkeleton variant="text" width="300px" height="32px" className="mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-md p-4">
              <LoadingSkeleton variant="text" width="50%" className="mb-2" />
              <LoadingSkeleton variant="text" width="80%" height="32px" />
            </div>
          ))}
        </div>
        <CardSkeleton count={2} />
      </div>
    </div>
  );
}

// Button loading spinner
export function ButtonSpinner({ size = "sm" }: { size?: "sm" | "md" | "lg" }) {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };

  return (
    <div className="flex items-center justify-center">
      <div
        className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-current border-t-transparent`}
      />
    </div>
  );
}
